/* eslint-disable @typescript-eslint/no-explicit-any */
import Color from 'color';
import { TRANSPARENT_COLOR } from 'elements/minute/sidebar/sidebarComponents/complex/ColorPalette';
import { recolorImageFromUrl } from 'hooks/marker/recolorImageFromUrl';
import { isEqual } from 'lodash';
import { useContext, useEffect, useRef, useState } from 'react';
import { IIndoorStandWorld, IWorldMarker } from 'react-give-me-map';
import { MapRef } from 'react-map-gl';
import { CtxEdit } from 'session/ctxEdit';
import { useUpdateEffect } from 'usehooks-ts';

const blobToFile = (theBlob: Blob, fileName: string): File => {
    // eslint-disable-next-line @typescript-eslint/no-explicit-any
    const b: any = theBlob;
    b.lastModifiedDate = new Date();
    b.name = fileName;
    return theBlob as File;
};

interface ImageSize {
    width: number;
    height: number;
}

interface CanvasDataEmoji {
    emoji: string;
    type: 'emoji';
    backgroundColor?: string;
    version: number;
}

interface CanvasDataIcon {
    src: string;
    color: string;
    backgroundColor?: string;
    type: 'icon';
    version: number;
}

interface CanvasDataImage {
    src: string;
    type: 'image';
    backgroundColor?: string;
    styleId?: string;
    version: number;
}
interface CanvasDataText {
    type: 'text';
    text: string;
    backgroundColor?: string;
    color: string;
    width: number;
    version: number;
}

type CanvasData = CanvasDataEmoji | CanvasDataIcon | CanvasDataImage | CanvasDataText;

interface NewImageGeneratedData {
    isSimpleImage: boolean;
    file: File;
    imageUrl: string;
    imageSize: ImageSize;
    isInitialLoad: boolean;
}

export interface UseIconPreviewParams {
    mapRef: React.RefObject<MapRef>;
    worldMarkers: IWorldMarker[];
    markerIds: string[];
    onNewImageGenerated?: (data: NewImageGeneratedData) => void;
    markerType: 'icon' | 'image';
    iconPrefix?: 'temp-icon' | 'temp-icon|';
    initialData: {
        generatedType?: 'icon' | 'image';
        generatedStyleId?: string | null;
        generatedBackgroundColor?: string | null;
        generatedIconUrl?: string | null;
        generatedImageUrl?: string | null;
        generatedEmoji?: string | null;
        generatedIconColor?: string | null;
        generatedText?: string | null;
        generatedTextColor?: string | null;
        generatedWidth?: number | null;
    };
}

export function mapIconPreviewIndoorStandPinToInitialDate(
    data: IIndoorStandWorld
): UseIconPreviewParams['initialData'] {
    return data.pinGeneratedType === 'icon'
        ? {
              generatedIconColor: data.pinGeneratedIconColor,
              generatedIconUrl: data.pinGeneratedIconUrl,
              generatedType: 'icon'
          }
        : {
              generatedImageUrl: data.pinGeneratedImageUrl,
              generatedType: 'image'
          };
}

export function mapIconPreviewIndoorStandLogoToInitialDate(
    data: IIndoorStandWorld
): UseIconPreviewParams['initialData'] {
    return {
        generatedStyleId: data.logoGeneratedStyleId,
        generatedImageUrl: data.logoGeneratedImageUrl,
        generatedType: 'image'
    };
}

export function useIconPreview(params: UseIconPreviewParams) {
    const mapRef = params.mapRef?.current;
    const ctxEdit = useContext(CtxEdit);
    const canvasRef = useRef<HTMLCanvasElement>(null);
    const isInitialLoad = useRef(true);
    const iconPrefix = params.iconPrefix ?? 'temp-icon';

    const markerIds = params.markerIds.filter(Boolean);

    const maxVersion = useRef<number | null>(null);

    const getInitialData = (): CanvasData => {
        if (params.initialData.generatedType === 'icon') {
            return {
                type: 'icon',
                src: params.initialData.generatedIconUrl,
                backgroundColor: params.initialData.generatedBackgroundColor,
                color: params.initialData.generatedIconColor,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        if (params.initialData.generatedType === 'image') {
            return {
                type: 'image',
                src: params.initialData.generatedImageUrl,
                backgroundColor: params.initialData.generatedBackgroundColor,
                styleId: params.initialData.generatedStyleId,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        if (params.initialData.generatedEmoji) {
            return {
                type: 'emoji',
                emoji: params.initialData.generatedEmoji,
                backgroundColor: params.initialData.generatedBackgroundColor,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        if (params.initialData.generatedIconUrl) {
            return {
                type: 'icon',
                src: params.initialData.generatedIconUrl,
                backgroundColor: params.initialData.generatedBackgroundColor,
                color: params.initialData.generatedIconColor,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        if (params.initialData.generatedImageUrl) {
            return {
                type: 'image',
                src: params.initialData.generatedImageUrl,
                backgroundColor: params.initialData.generatedBackgroundColor,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        if (params.initialData.generatedWidth) {
            return {
                type: 'text',
                color: params.initialData.generatedTextColor,
                text: params.initialData.generatedText,
                width: params.initialData.generatedWidth,
                backgroundColor: params.initialData.generatedBackgroundColor,
                version: maxVersion.current ? maxVersion.current + 1 : 0
            };
        }
        return null;
    };

    const [imageData, setImageData] = useState<ImageData>(null);

    const [picture, setPicture] = useState<CanvasData | null>(getInitialData());

    useUpdateEffect(() => {
        const newPicture = getInitialData();

        const isDifferent = !isEqual(newPicture, picture);

        if (isDifferent) {
            setPicture(newPicture);
        }
    }, [
        params.initialData.generatedBackgroundColor,
        params.initialData.generatedEmoji,
        params.initialData.generatedIconColor,
        params.initialData.generatedIconUrl,
        params.initialData.generatedImageUrl,
        params.initialData.generatedText,
        params.initialData.generatedWidth,
        params.initialData.generatedStyleId,
        params.initialData.generatedType
    ]);

    useEffect(() => {
        if (params.markerType !== 'icon') {
            return;
        }

        const onImageMissing = async (params: { id: string }) => {
            const isTempIcon = params.id.startsWith(iconPrefix);
            if (!isTempIcon) {
                return;
            }

            const markerId = params.id.substring(iconPrefix.length + 1);

            if (imageData && !mapRef?.hasImage(params.id) && markerIds.includes(markerId)) {
                mapRef?.addImage(params.id, imageData);
            }
        };

        mapRef?.on('styleimagemissing', onImageMissing);
        return () => {
            mapRef?.off('styleimagemissing', onImageMissing);
        };
    }, [mapRef, markerIds, imageData]);

    useEffect(() => {
        if (!imageData || params.markerType !== 'icon') {
            return;
        }

        markerIds.forEach((markerId) => {
            if (mapRef?.hasImage(`${iconPrefix}-${markerId}`)) {
                mapRef?.updateImage(`${iconPrefix}-${markerId}`, imageData);
            } else {
                mapRef?.addImage(`${iconPrefix}-${markerId}`, imageData);
            }
        });
    }, [imageData]);

    function generateUploadImage(imageSize: ImageSize, picture: CanvasData) {
        const isSimpleImage =
            picture.type === 'image' &&
            (!picture.backgroundColor || picture.backgroundColor === TRANSPARENT_COLOR);

        const imageUrl = isSimpleImage
            ? picture.src
            : canvasRef.current.toDataURL('image/png').replace('image/png', 'image/octet-stream');
        const onBlobDone = async (blob: Blob) => {
            if (picture.version < maxVersion.current) {
                return;
            }
            const file = blobToFile(blob, 'marker.png');
            if (ctxEdit.worldMarkers.some((marker) => marker.id === markerIds[0])) {
                params.onNewImageGenerated?.({
                    isSimpleImage,
                    file,
                    imageUrl,
                    imageSize,
                    isInitialLoad: isInitialLoad.current
                });
            }

            if (isInitialLoad.current) {
                isInitialLoad.current = false;
            }
        };

        const imgFormat = 'image/png';
        const imgQuality = 1;

        canvasRef.current?.toBlob(onBlobDone, imgFormat, imgQuality);
    }

    function generateImage(
        canvasCtx: CanvasRenderingContext2D,
        imageSize: ImageSize,
        picture: CanvasData
    ) {
        const imageData = canvasCtx?.getImageData(0, 0, imageSize.width, imageSize.height);

        setImageData(imageData);

        generateUploadImage(imageSize, picture);
    }

    useEffect(() => {
        if (!picture) {
            return;
        }

        if (maxVersion.current !== null && picture.version <= maxVersion.current) {
            return;
        }

        maxVersion.current = picture.version;

        if (!canvasRef.current) {
            return;
        }

        const BACKGROUND_PADDING = 42 * ((canvasRef.current?.width ?? 200) / 200);

        const hasBackground =
            picture.backgroundColor && picture.backgroundColor !== TRANSPARENT_COLOR;

        const canvasCtx = canvasRef.current?.getContext('2d', { willReadFrequently: true });
        const canvasWidth = canvasRef.current?.width;
        const canvasHeight = canvasRef.current?.height;

        canvasCtx.clearRect(0, 0, canvasWidth, canvasHeight);

        function paintCircleBackground(width: number, height: number) {
            canvasCtx.beginPath();
            if (hasBackground) {
                canvasCtx.ellipse(width, height, width, height, 0, 0, 2 * Math.PI);
                canvasCtx.fillStyle = picture.backgroundColor;
                canvasCtx.fill();
            }
        }

        function paintFullBackground(width: number, height: number, scale: number) {
            canvasCtx.beginPath();
            if (hasBackground) {
                // eslint-disable-next-line @typescript-eslint/no-explicit-any
                (canvasCtx as any).roundRect(0, 0, width, height, [(10 * scale) / 2]);
                canvasCtx.fillStyle = picture.backgroundColor;
                canvasCtx.fill();
            }
        }

        if (picture.type === 'emoji') {
            paintCircleBackground(canvasWidth / 2, canvasHeight / 2);
            canvasCtx.fillStyle = '#000';
            canvasCtx.font = `100px serif`;
            canvasCtx.textAlign = 'center';
            canvasCtx.textBaseline = 'hanging';
            canvasCtx.fillText(picture.emoji, canvasWidth / 2, (0.62 * canvasHeight) / 2);
            generateImage(canvasCtx, { width: canvasWidth, height: canvasHeight }, picture);
        } else if (picture.type === 'icon') {
            paintCircleBackground(canvasWidth / 2, canvasHeight / 2);
            canvasCtx.fillStyle = picture.color;
            recolorImageFromUrl({
                color: picture.color,
                canvasHeight,
                canvasWidth,
                src: picture.src
            }).then((image) => {
                const padding = hasBackground ? BACKGROUND_PADDING : 0;
                canvasCtx.drawImage(
                    // eslint-disable-next-line @typescript-eslint/no-explicit-any
                    image as any,
                    padding,
                    padding,
                    canvasWidth - 2 * padding,
                    canvasHeight - 2 * padding
                );
                generateImage(canvasCtx, { width: canvasWidth, height: canvasHeight }, picture);
            });
        } else if (picture.type === 'text') {
            const scaleFactor = 14;
            const xPadding = 100;
            const defaultCanvaswidth = picture.width;

            const fontSize = 12 * scaleFactor;

            canvasCtx.font = fontSize + 'px sans-serif'; // set font
            canvasCtx.textAlign = 'center';
            canvasCtx.textBaseline = 'middle';

            const lineHeight = canvasCtx.measureText('M').width * 1.4;
            const lines = (picture.text || ' ').split('\n');

            const newCanvasWidth = defaultCanvaswidth * scaleFactor + xPadding;

            const finalLines = [];

            for (const line of lines) {
                if (canvasCtx.measureText(line).width + 2 * xPadding <= newCanvasWidth) {
                    finalLines.push(line);
                } else {
                    const words = line.split(/(\s)/);
                    let currentLine = [];
                    for (const word of words) {
                        if (!currentLine.length) {
                            currentLine.push(word);
                            continue;
                        }
                        const width = canvasCtx.measureText(
                            [...currentLine, word].join('').trim()
                        ).width;
                        if (width + 2 * xPadding > newCanvasWidth) {
                            finalLines.push(currentLine.join('').trim());
                            currentLine = [word];
                        } else {
                            currentLine.push(word);
                        }
                    }
                    finalLines.push(currentLine.join('').trim());
                }
            }

            const yPadding = 100;
            const newCanvasHeight = finalLines.length * lineHeight + 2 * yPadding;
            canvasRef.current.width = newCanvasWidth;
            canvasRef.current.height = newCanvasHeight;

            paintFullBackground(newCanvasWidth, newCanvasHeight, scaleFactor);

            const getShadow = () => {
                return Color(picture.color).isLight() ? '#000' : '#fff';
            };
            const shadow = getShadow();

            canvasCtx.fillStyle = picture.color;
            canvasCtx.font = fontSize + 'px sans-serif'; // set font
            canvasCtx.textAlign = 'center';
            canvasCtx.textBaseline = 'middle';

            if (shadow) {
                canvasCtx.shadowColor = shadow;
                canvasCtx.shadowOffsetX = 1;
                canvasCtx.shadowOffsetY = 1;
                canvasCtx.shadowBlur = 1;
            }

            const x = newCanvasWidth / 2;
            let y = yPadding + lineHeight / 2;
            for (let i = 0; i < finalLines.length; ++i) {
                canvasCtx.fillText(finalLines[i], x, y);
                y += lineHeight;
            }

            generateImage(
                canvasCtx,
                {
                    width: newCanvasWidth,
                    height: newCanvasHeight
                },
                picture
            );
        } else if (picture.type === 'image' && picture.styleId === 'default') {
            const myImg = new Image();
            myImg.onload = function () {
                let height = canvasHeight;
                let width = height * (myImg.naturalWidth / myImg.naturalHeight);

                if (width > canvasWidth) {
                    const scale = canvasWidth / width;
                    width = scale * width;
                    height = scale * height;
                }

                const left = (canvasWidth - width) / 2;
                const top = (canvasHeight - height) / 2;

                canvasCtx.drawImage(myImg, left, top, width, height);

                generateImage(
                    canvasCtx,
                    {
                        width: canvasWidth,
                        height: canvasHeight
                    },
                    picture
                );
            };
            myImg.crossOrigin = 'Anonymous';
            myImg.src = picture.src;
        } else if (picture.type === 'image' && picture.styleId === 'white-box') {
            const myImg = new Image();
            myImg.onload = function () {
                const shadowBlur = 4;
                const shadowWidth = 4;
                const paddingMinX = 10;
                const paddingMinY = 5;
                const borderRadius = 10;

                let height = canvasHeight - 2 * (shadowWidth + paddingMinY);
                let width = height * (myImg.naturalWidth / myImg.naturalHeight);

                const maxWidth = canvasWidth - 2 * (shadowWidth + paddingMinX);

                if (width > maxWidth) {
                    const scale = maxWidth / width;
                    width = scale * width;
                    height = scale * height;
                }

                const left = (canvasWidth - width) / 2;
                const top = (canvasHeight - height) / 2;

                // render rect for shadow
                canvasCtx.beginPath();
                canvasCtx.shadowColor = '#ccc';
                canvasCtx.shadowBlur = shadowBlur;
                canvasCtx.fillStyle = 'rgba(0,0,0,.4)';
                (canvasCtx as any).roundRect(
                    shadowWidth,
                    shadowWidth,
                    canvasWidth - 2 * shadowWidth,
                    canvasHeight - 2 * shadowWidth,
                    [borderRadius]
                );
                canvasCtx.fill();

                // render rect
                canvasCtx.beginPath();
                (canvasCtx as any).roundRect(
                    shadowWidth,
                    shadowWidth,
                    canvasWidth - 2 * shadowWidth,
                    canvasHeight - 2 * shadowWidth,
                    [borderRadius]
                );
                canvasCtx.fillStyle = '#fff';
                canvasCtx.fill();

                canvasCtx.shadowColor = null;
                canvasCtx.shadowBlur = null;

                // render image
                canvasCtx.drawImage(myImg, left, top, width, height);

                generateImage(
                    canvasCtx,
                    {
                        width: canvasWidth,
                        height: canvasHeight
                    },
                    picture
                );
            };
            myImg.crossOrigin = 'Anonymous';
            myImg.src = picture.src;
        } else if (picture.type === 'image') {
            paintCircleBackground(canvasWidth / 2, canvasHeight / 2);

            const padding = hasBackground ? BACKGROUND_PADDING : 0;
            const myImg = new Image();
            myImg.onload = function () {
                let width = canvasWidth - 2 * padding;
                let height = canvasHeight - 2 * padding;

                if (myImg.naturalWidth / myImg.naturalHeight > 1) {
                    height = width * (myImg.naturalHeight / myImg.naturalWidth);
                }
                if (myImg.naturalHeight / myImg.naturalWidth > 1) {
                    width = height * (myImg.naturalWidth / myImg.naturalHeight);
                }

                let newCanvasHeight = canvasWidth;
                if (params.markerType === 'image') {
                    if (hasBackground) {
                        canvasRef.current.height = canvasHeight;
                    } else {
                        newCanvasHeight = height + 2 * padding;
                        // This clears the canvas, we need to repaint the background
                        canvasRef.current.height = canvasHeight;
                    }
                }

                paintCircleBackground(canvasWidth / 2, newCanvasHeight / 2);

                const left = (canvasWidth - width) / 2;
                const top =
                    params.markerType === 'image' || hasBackground
                        ? (canvasHeight - height) / 2
                        : canvasHeight - height;

                canvasCtx.drawImage(myImg, left, top, width, height);

                generateImage(
                    canvasCtx,
                    {
                        width: canvasWidth,
                        height: canvasHeight
                    },
                    picture
                );
            };
            myImg.crossOrigin = 'Anonymous';
            myImg.src = picture.src;
        }
    }, [picture?.version ?? 0]);

    function setIcon(iconSrc: string, color: string) {
        setPicture((curr) => ({
            ...curr,
            type: 'icon',
            src: iconSrc,
            color,
            version: curr?.version != null ? curr.version + 1 : 0
        }));
    }

    function setIconColor(color: string) {
        setPicture((curr) => {
            if (curr.type === 'icon') {
                return { ...curr, color, version: curr?.version != null ? curr.version + 1 : 0 };
            }
            return curr;
        });
    }

    function setEmoji(emoji: string) {
        setPicture((curr) => ({
            ...curr,
            type: 'emoji',
            emoji,
            version: curr?.version != null ? curr.version + 1 : 0
        }));
    }

    function setBackgroundColor(color: string) {
        setPicture((curr) => ({
            ...curr,
            backgroundColor: color,
            version: curr?.version != null ? curr.version + 1 : 0
        }));
    }

    function setImage(imageUrl: string) {
        setPicture((curr) => ({
            ...curr,
            type: 'image',
            src: imageUrl,
            version: curr?.version != null ? curr.version + 1 : 0
        }));
    }

    function setTextColor(color: string) {
        setPicture((curr) => {
            if (curr.type === 'text') {
                return { ...curr, color, version: curr?.version != null ? curr.version + 1 : 0 };
            }
            return curr;
        });
    }

    function setText(text: string) {
        if (picture.type === 'text') {
            setPicture((curr) => {
                if (picture.type === 'text') {
                    return { ...curr, text, version: curr?.version != null ? curr.version + 1 : 0 };
                }
                return curr;
            });
        }
    }

    function setWidth(width: number) {
        setPicture((curr) => {
            if (picture.type === 'text') {
                return { ...curr, width, version: curr?.version != null ? curr.version + 1 : 0 };
            }
            return curr;
        });
    }

    function setStyleId(styleId: string) {
        setPicture((curr) => {
            if (picture.type === 'image') {
                return { ...curr, styleId, version: curr?.version != null ? curr.version + 1 : 0 };
            }
            return curr;
        });
    }

    function setType(data: Omit<CanvasDataImage, 'version'> | Omit<CanvasDataIcon, 'version'>) {
        setPicture({ ...data, version: picture?.version != null ? picture.version + 1 : 0 });
    }

    return {
        setIcon,
        setIconColor,
        setEmoji,
        setBackgroundColor,
        setImage,
        setStyleId,
        setType,
        setTextColor,
        setText,
        setWidth,
        canvasRef,
        picture
    };
}
